import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileText,
  Calculator,
  BookOpen,
  Briefcase,
  Check,
  Phone,
  MessageSquare,
  FolderOpen,
  CheckCircle2,
  Rocket,
  Building2,
  Truck,
  UserRoundCog,
  Shirt,
  ShoppingCart,
  Headphones,
  Plane,
  ShieldCheck,
  LineChart,
  Clock,
  FileBarChart2,
  Quote,
  Star,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import Reveal from "@/components/site/Reveal";
import AnimatedCounter from "@/components/site/AnimatedCounter";
import { insights } from "@/data/insights";

// ---- DATA ----
const metrics = [
  { k: "50+", l: "Businesses Served" },
  { k: "100+", l: "Filings Completed" },
  { k: "4", l: "Core Practice Areas" },
  { k: "End-to-End", l: "Compliance Support" },
];

const services = [
  {
    icon: FileText,
    title: "GST Services",
    desc: "Stay fully compliant with monthly returns, reconciliations and notices — handled end-to-end.",
    bullets: ["Registration & filings", "GSTR reconciliations", "Notices & representations"],
  },
  {
    icon: Calculator,
    title: "Income Tax Filing",
    desc: "Accurate returns with year-round planning so tax is never a last-minute scramble.",
    bullets: ["ITR & TDS returns", "Advance tax planning", "Capital gains advisory"],
  },
  {
    icon: BookOpen,
    title: "Accounting & Bookkeeping",
    desc: "Clean, decision-ready books and monthly MIS so you always know where you stand.",
    bullets: ["Ledger & reconciliations", "Financial statements", "MIS reports"],
  },
  {
    icon: Briefcase,
    title: "Business Advisory",
    desc: "Practical guidance on structure, cost, compliance and long-term growth strategy.",
    bullets: ["Entity structuring", "Cost optimization", "Growth strategy"],
  },
];

const pillars = [
  { title: "Timely Compliance", desc: "Deadlines tracked, never missed." },
  { title: "Structured Approach", desc: "Clear process from onboarding to delivery." },
  { title: "Clear Communication", desc: "Straightforward updates, no jargon." },
  { title: "Practical Support", desc: "Advice grounded in your business reality." },
];

const steps = [
  { icon: Phone, title: "Contact", desc: "Reach out via call, email or WhatsApp." },
  { icon: MessageSquare, title: "Understand Requirement", desc: "A short discovery to scope your needs." },
  { icon: FolderOpen, title: "Document Collection", desc: "We share a checklist and guide you through." },
  { icon: CheckCircle2, title: "Work Completion", desc: "Delivery with clarity, on time, every time." },
];

const segments = [
  { icon: Rocket, title: "Startups & New Businesses", desc: "Registrations, first-year compliance and foundation setup." },
  { icon: Building2, title: "SMEs & Growing Companies", desc: "Monthly compliance, MIS and financial hygiene at scale." },
  { icon: Truck, title: "Traders & Manufacturers", desc: "GST, inventory-linked books and vendor reconciliations." },
  { icon: UserRoundCog, title: "Professionals & Consultants", desc: "Income tax, presumptive schemes and advisory." },
];

const industries = [
  { icon: Shirt, title: "Textile & Manufacturing", desc: "Complex GST, inventory and labour-linked compliance." },
  { icon: ShoppingCart, title: "E-commerce & Trading", desc: "Marketplace GST, TCS reconciliations and returns." },
  { icon: Headphones, title: "Service Businesses", desc: "TDS, GST on services and professional tax filings." },
  { icon: Plane, title: "Import-Export Businesses", desc: "IEC, customs-aligned books and forex reporting." },
];

const outcomes = [
  { icon: ShieldCheck, title: "Reduce Compliance Risk", desc: "Deadlines, notices and documentation — handled with discipline, so you sleep better." },
  { icon: LineChart, title: "Improve Financial Clarity", desc: "Clean books and monthly MIS give you a real view of revenue, cost and profit." },
  { icon: Clock, title: "Save Time for Owners", desc: "Offload tax and compliance execution — reinvest that time into your business." },
  { icon: FileBarChart2, title: "Structured Reporting", desc: "Consistent formats across GST, TDS, ROC and financial reports — audit-ready always." },
];

const testimonials = [
  {
    quote:
      "Avvanta replaced three disjointed vendors for us. Our books, GST and TDS are now on a single predictable rhythm — month-end is no longer stressful.",
    name: "Director",
    role: "Textile Manufacturing · Surat",
  },
  {
    quote:
      "They didn't just file our returns — they explained them. For the first time, our leadership actually understands the numbers behind the compliance.",
    name: "Founder",
    role: "Professional Services Firm",
  },
  {
    quote:
      "Clear process, zero chasing. Checklists, timelines and follow-ups are all structured. Exactly what a growing business needs from its advisors.",
    name: "Managing Partner",
    role: "Trading & Distribution",
  },
];

// ---- PAGE ----
export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1760561149141-0e88e5e27026?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwc2t5bGluZXxlbnwwfHx8fDE3Nzc4ODgwNjJ8MA&ixlib=rb-4.1.0&q=85')",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#0A1F44]/80" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,.4) 1px, transparent 1px)",
            backgroundSize: "72px 100%",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full page-offset pb-24 md:pb-32">
          <div className="max-w-3xl reveal">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold"
            >
              <span className="h-px w-8 bg-[#D4AF37]" />
              Advise · Assist · Achieve
            </span>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display font-light text-white text-[40px] sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight"
            >
              Helping Businesses Stay{" "}
              <span className="text-[#D4AF37] font-medium">Compliant, Organized</span>{" "}
              <span className="italic font-light">&amp; Growth-Ready</span>
            </h1>
            <p
              data-testid="hero-subtext"
              className="mt-6 text-white/80 text-base md:text-lg font-medium tracking-wide"
            >
              GST <span className="text-[#D4AF37] mx-2">|</span> Income Tax
              <span className="text-[#D4AF37] mx-2">|</span> Accounting
              <span className="text-[#D4AF37] mx-2">|</span> Compliance
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A1F44] px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-white transition-colors"
              >
                Get Consultation
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between text-white/60 text-[11px] uppercase tracking-[0.3em]">
            <span>Avvanta Advisory</span>
            <span className="hidden md:inline">Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* AUTHORITY / METRICS STRIP */}
      <section data-testid="authority-section" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8">
          {metrics.map((m, i) => (
            <Reveal key={m.l} delay={i * 80}>
              <div
                data-testid={`metric-${i}`}
                className="relative pl-6 md:pl-8"
              >
                <span
                  className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#D4AF37]"
                  aria-hidden="true"
                />
                <div className="font-display text-[#0A1F44] text-3xl md:text-4xl lg:text-[40px] font-medium tracking-tight leading-none">
                  <AnimatedCounter value={m.k} />
                </div>
                <div className="mt-3 text-[11px] md:text-[12px] uppercase tracking-[0.22em] text-[#4B5563] font-semibold">
                  {m.l}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Our Services"
            title="Focused expertise for growing businesses."
            description="Four core practice areas, delivered with discipline and clarity — under one accountable team."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  data-testid={`home-service-card-${i}`}
                  className="card-hover bg-white p-8 border border-gray-200 group flex flex-col"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F8F9FA] text-[#0A1F44] group-hover:bg-[#0A1F44] group-hover:text-[#D4AF37] transition-colors">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-xl text-[#0A1F44] font-medium">{s.title}</h3>
                  <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed">{s.desc}</p>
                  <div className="mt-5 h-px w-10 bg-[#D4AF37]" />
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[13px] text-[#111827]">
                        <Check size={14} className="mt-[3px] text-[#D4AF37]" strokeWidth={2.25} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/services"
                    className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-[#0A1F44] group-hover:text-[#D4AF37] transition-colors"
                  >
                    Learn more <ArrowRight size={14} />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLIENT SEGMENTS */}
      <section data-testid="segments-section" className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Who We Work With"
            title="Advisory built around your stage and scale."
            description="Whether you are just starting up or managing a mature enterprise, our engagement adapts to your reality."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {segments.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`segment-${i}`}
                  className="card-hover bg-white border border-gray-200 p-8 group"
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-[#D4AF37] text-[#0A1F44] group-hover:bg-[#0A1F44] group-hover:text-[#D4AF37] transition-colors">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-lg md:text-xl text-[#0A1F44] font-medium">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="industries-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Industries We Serve"
            title="Sector-aware advisory — not one-size-fits-all."
            description="We understand the nuances each sector brings to books, tax and compliance."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div
                  key={ind.title}
                  data-testid={`industry-${i}`}
                  className="card-hover relative bg-white border border-gray-200 p-8 group overflow-hidden"
                >
                  <div
                    className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#F8F9FA] opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-hidden="true"
                  />
                  <div className="relative w-11 h-11 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37]">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="relative mt-6 font-display text-lg md:text-xl text-[#0A1F44] font-medium">
                    {ind.title}
                  </h3>
                  <p className="relative mt-3 text-[14px] text-[#4B5563] leading-relaxed">{ind.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-choose-section" className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading
              eyebrow="Why Choose Us"
              title="A steady hand for your compliance and finance."
              description="We combine discipline with practical advice so you can focus on running your business — not chasing deadlines."
            />
            <Link
              to="/about"
              data-testid="why-learn-more"
              className="inline-flex items-center gap-3 mt-10 border border-[#0A1F44] text-[#0A1F44] px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.16em] hover:bg-[#0A1F44] hover:text-white transition-colors"
            >
              Learn About Us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="bg-white border border-gray-200 p-7 card-hover"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37]">
                  <Check size={18} strokeWidth={2} />
                </div>
                <h4 className="mt-5 font-display text-lg text-[#0A1F44] font-medium">{p.title}</h4>
                <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE ADD VALUE */}
      <section data-testid="value-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="How We Add Value"
            title="Outcomes that matter beyond filing a return."
            description="We measure success in the clarity, time and confidence we create for business owners."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {outcomes.map((o, i) => {
              const Icon = o.icon;
              return (
                <div
                  key={o.title}
                  data-testid={`value-card-${i}`}
                  className="flex gap-6 p-8 bg-[#F8F9FA] border border-gray-200 card-hover"
                >
                  <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-white border border-[#D4AF37] text-[#0A1F44]">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-display text-lg md:text-xl text-[#0A1F44] font-medium">{o.title}</h4>
                    <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{o.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="How It Works"
            title="A simple, structured path from query to delivery."
            align="center"
          />
          <div className="mt-16 relative grid grid-cols-1 md:grid-cols-4 gap-10">
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-[28px] left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent"
            />
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`step-${i + 1}`} className="relative text-center">
                  <div className="relative mx-auto w-14 h-14 rounded-full bg-white border border-[#D4AF37] text-[#0A1F44] flex items-center justify-center z-10">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                    Step {i + 1}
                  </div>
                  <h4 className="mt-2 font-display text-lg md:text-xl text-[#0A1F44] font-medium">
                    {s.title}
                  </h4>
                  <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed max-w-[240px] mx-auto">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="What Clients Say"
            title="Trusted by founders and finance leaders."
            description="Short excerpts from the kind of conversations we have every month."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="relative bg-[#F8F9FA] border border-gray-200 p-8 md:p-10 card-hover"
              >
                <Quote size={32} className="text-[#D4AF37]" strokeWidth={1.25} />
                <blockquote className="mt-5 text-[15px] md:text-[16px] text-[#111827] leading-relaxed">
                  "{t.quote}"
                </blockquote>
                <div className="mt-6 flex items-center gap-1 text-[#D4AF37]">
                  {[0, 1, 2, 3, 4].map((k) => (
                    <Star key={k} size={14} fill="#D4AF37" strokeWidth={0} />
                  ))}
                </div>
                <figcaption className="mt-5 border-t border-gray-200 pt-5">
                  <div className="font-display text-[#0A1F44] font-medium">{t.name}</div>
                  <div className="text-[12px] uppercase tracking-[0.22em] text-[#4B5563] mt-1">
                    {t.role}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / SURAT SECTION */}
      <section
        data-testid="trust-section"
        className="relative py-28 md:py-36 bg-[#0A1F44] text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1554224154-22dec7ec8818?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHx0YXglMjBjb21wbGlhbmNlJTIwYnVzaW5lc3MlMjBkb2N1bWVudHN8ZW58MHx8fHwxNzc3ODg4MDY4fDA&ixlib=rb-4.1.0&q=85')",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
            Trusted locally
          </span>
          <h2 className="mt-6 font-display text-3xl md:text-5xl lg:text-[52px] leading-[1.1] tracking-tight">
            Serving businesses across{" "}
            <span className="text-[#D4AF37]">Surat</span> with structured compliance support.
          </h2>
          <div className="mt-8 h-[2px] w-14 bg-[#D4AF37] mx-auto" />
          <p className="mt-6 text-white/70 max-w-2xl mx-auto text-[15px] md:text-base leading-relaxed">
            From GST and income tax to bookkeeping and registrations — we provide reliable,
            structured support so your business runs smoothly and compliantly.
          </p>
        </div>
      </section>

      {/* INSIGHTS TEASER */}
      <section data-testid="insights-teaser" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <SectionHeading
                eyebrow="Insights & Resources"
                title="Short, practical reads from our practice."
                description="Evergreen notes on GST, tax, accounting and project finance — written for busy founders."
              />
            </Reveal>
            <Reveal delay={120}>
              <Link
                to="/insights"
                data-testid="insights-view-all"
                className="inline-flex items-center gap-2 border border-[#0A1F44] text-[#0A1F44] px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] hover:bg-[#0A1F44] hover:text-white transition-colors"
              >
                View All Insights <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {insights.slice(0, 3).map((post, i) => (
              <Reveal key={post.slug} delay={i * 100}>
                <Link
                  to={`/insights/${post.slug}`}
                  data-testid={`insight-teaser-${i}`}
                  className="group block bg-white border border-gray-200 overflow-hidden h-full transition-all duration-500 hover:-translate-y-1 hover:border-[#D4AF37] hover:shadow-[0_30px_80px_-30px_rgba(10,31,68,0.25)]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#0A1F44]">
                    <img
                      src={post.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-70 group-hover:scale-[1.05] transition-all duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/40 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center bg-[#D4AF37] text-[#0A1F44] text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#4B5563]">
                      {post.date} · {post.readTime}
                    </div>
                    <h3 className="mt-3 font-display text-lg md:text-xl text-[#0A1F44] font-medium leading-tight">
                      {post.title}
                    </h3>
                    <div className="mt-5 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#0A1F44] group-hover:text-[#D4AF37] transition-colors">
                      Read article <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STRONG CTA BEFORE FOOTER */}
      <section data-testid="final-cta-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="relative bg-[#F8F9FA] border border-gray-200 p-10 md:p-16 overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full w-[3px] bg-[#D4AF37]"
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
                  Let's Talk
                </span>
                <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-[44px] leading-[1.1] text-[#0A1F44] tracking-tight">
                  Looking for reliable compliance and{" "}
                  <span className="italic font-light">financial support?</span>
                </h2>
                <p className="mt-5 text-[15px] md:text-base text-[#4B5563] leading-relaxed max-w-2xl">
                  Share a few details and we'll respond within one business day with a clear next step.
                  No lengthy sales process — just structured, practical advice.
                </p>
              </div>
              <div className="lg:col-span-2 flex flex-col gap-4 lg:items-end">
                <Link
                  to="/contact"
                  data-testid="final-cta-button"
                  className="w-full lg:w-auto inline-flex items-center justify-center gap-3 bg-[#0A1F44] text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors"
                >
                  Book Consultation <ArrowRight size={18} />
                </Link>
                <a
                  href="tel:+917487978020"
                  data-testid="final-cta-phone"
                  className="w-full lg:w-auto inline-flex items-center justify-center gap-3 border border-[#0A1F44] text-[#0A1F44] px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  <Phone size={16} strokeWidth={1.75} /> +91 74879 78020
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
